import { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserCircle, faSun, faMoon } from '@fortawesome/free-regular-svg-icons';
import { faList, faSignOutAlt, faHouse, faTruck, faDriversLicense, faUsersBetweenLines, faCode, faWarehouse, faComments, faRoute, faHeadset, faTruckArrowRight, faGraduationCap, faFileInvoiceDollar } from '@fortawesome/free-solid-svg-icons';
import logger from "../utils/logger";

import { useAuthProvider } from '../providers/AuthProvider';
import { check_pending_orders } from '../api/notifications';
import { getConversations } from '../api/messages';
import { useSupabase } from '../providers/SupabaseProvider';

import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';

import { ActivityIndicator } from '../components/custom/ActivityIndicator';

import { useTheme } from './themeControl/ThemeProvider';
import { useAlert } from '../providers/AlertProvider';

import { getSessionUUID } from '../components/lib/AuthToken';

import { Time, Day } from './LiveUpdates';
import TetrisGame from './custom/TetrisGame';

import './css/NavStyles.css';
import './css/HomeStyles.css';

import Logo from '../components/img/LogiQOS_logo.png';
import LogoDark from '../components/img/LogiQOS_logo-dark.png';


// Remote Assistance — single dropdown entry that opens an OS picker modal
const RemoteAssistanceItem = ({ theme }) => {
    const [showPicker, setShowPicker]     = useState(false);
    const [showLinux, setShowLinux]       = useState(false);
    const [linuxCommand, setLinuxCommand] = useState('');
    const [copied, setCopied]             = useState(false);

    const itemStyle  = { color: theme === 'dark' ? '#fff' : '#191a1a', cursor: 'pointer' };
    const modalStyle = {
        background: theme === 'dark' ? '#1e1e1e' : '#f5f5f5',
        color: theme === 'dark' ? '#fff' : '#191a1a',
        border: '1px solid #555',
        borderRadius: '8px',
        padding: '24px',
        maxWidth: '420px',
        width: '90%',
    };

    const openPicker = (e) => {
        e.stopPropagation();
        setShowLinux(false);
        setShowPicker(true);
    };

    const handleDownload = async (url, filename) => {
        setShowPicker(false);
        const session_uuid = getSessionUUID();
        const res = await fetch(url, { headers: { 'Authorization': `Bearer ${session_uuid}` } });
        if (!res.ok) return;
        const blob = await res.blob();
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    const handleLinuxClick = async () => {
        if (!linuxCommand) {
            const session_uuid = getSessionUUID();
            const res = await fetch('/remote_support/linux', {
                headers: { 'Authorization': `Bearer ${session_uuid}` }
            });
            const data = await res.json();
            setLinuxCommand(data.command);
        }
        setShowPicker(false);
        setShowLinux(true);
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(linuxCommand);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const btnBase = { padding: '10px 0', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: 500, fontSize: '0.9rem' };

    return (
        <>
            <span className="dropdown-item" style={itemStyle} onClick={openPicker}>
                <FontAwesomeIcon icon={faHeadset} style={{ width: '24px' }} /> Remote Assistance
            </span>

            {/* OS picker modal */}
            {showPicker && createPortal(
                <div onClick={() => setShowPicker(false)}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div onClick={e => e.stopPropagation()} style={modalStyle}>
                        <h6 style={{ marginBottom: '6px' }}><FontAwesomeIcon icon={faHeadset} style={{ width: '24px' }} /> Remote Assistance</h6>
                        <p style={{ fontSize: '0.85rem', opacity: 0.7, marginBottom: '18px' }}>Select your operating system:</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <button style={{ ...btnBase, background: '#1997eb', color: '#fff' }}
                                onClick={() => handleDownload('/remote_support/windows', 'LogiqOS-Remote-Support.exe')}>
                                Windows
                            </button>
                            <button style={{ ...btnBase, background: '#555', color: '#fff' }}
                                onClick={() => handleDownload('/remote_support/mac', 'LogiqOC-Remote-Support-Mac.zip')}>
                                Mac
                            </button>
                            <button style={{ ...btnBase, background: '#e67e22', color: '#fff' }}
                                onClick={handleLinuxClick}>
                                Linux
                            </button>
                            <button style={{ ...btnBase, background: 'transparent', color: theme === 'dark' ? '#aaa' : '#666', border: '1px solid #aaa' }}
                                onClick={() => setShowPicker(false)}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Linux command modal */}
            {showLinux && createPortal(
                <div onClick={() => setShowLinux(false)}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div onClick={e => e.stopPropagation()} style={{ ...modalStyle, maxWidth: '560px' }}>
                        <h6 style={{ marginBottom: '10px' }}><FontAwesomeIcon icon={faHeadset} style={{ width: '24px' }} /> Remote Assistance — Linux</h6>
                        <p style={{ fontSize: '0.85rem', opacity: 0.75 }}>Copy the command below and paste it into a terminal:</p>
                        <textarea
                            readOnly
                            value={linuxCommand}
                            rows={4}
                            style={{
                                width: '100%', fontFamily: 'monospace', fontSize: '0.8rem',
                                background: theme === 'dark' ? '#111' : '#e8e8e8',
                                color: theme === 'dark' ? '#0f0' : '#191a1a',
                                border: 'none', borderRadius: '4px', padding: '8px', resize: 'none',
                            }}
                        />
                        <div style={{ display: 'flex', gap: '10px', marginTop: '12px', justifyContent: 'flex-end' }}>
                            <button onClick={handleCopy}
                                style={{ padding: '6px 14px', borderRadius: '4px', border: 'none', background: copied ? '#28a745' : '#1997eb', color: '#fff', cursor: 'pointer' }}>
                                {copied ? 'Copied!' : 'Copy'}
                            </button>
                            <button onClick={() => setShowLinux(false)}
                                style={{ padding: '6px 14px', borderRadius: '4px', border: 'none', background: '#555', color: '#fff', cursor: 'pointer' }}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

// Links
const LoggedInLinks=()=>{

    const { theme, setTheme } = useTheme();

    const appLogo = theme === 'dark' ? LogoDark : Logo;

    const navigate = useNavigate(); // Hook for navigation
    const { showAlert } = useAlert(); // Hook for alert
    const { isAdmin, handleLogout, isDev, isManager, isCustomer, isWarehouse, isService, features, hasCustomerWarehouseAccess, permissions } = useAuthProvider(); // Hook for admin status
    const supabase = useSupabase();
    const [isLoading, setIsLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [pendingOrdersCount, setPendingOrdersCount] = useState(0);
    const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);

    // dropdown menu
    const toggleDropdown = () => {
        // Define the toggleDropdown function
        setShowDropdown((prev) => !prev);
    };


    // Theme
    const toggleTheme = () => {
        const newTheme = theme === 'dark' ? 'light' : 'dark';
        setTheme(newTheme); // Update theme in context
        localStorage.setItem('theme', newTheme); // Update theme in local storage
    
        const session_uuid = getSessionUUID();
    
        if (!session_uuid) {
            showAlert("Failed to get access token");
            handleLogout();
            navigate('/'); // Redirect to login page
            return;
        }
    
        fetch('/profile/update_mode', {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session_uuid}`,
            },
            body: JSON.stringify({ dark_mode: newTheme === 'dark' }),
        }).catch(err => logger.error("Failed to update theme on server:", err));
    }

    // Function to fetch pending orders count
    const fetchPendingOrders = async () => {
        // Only fetch if user is not a customer
        if (isCustomer) {
            return;
        }
        
        try {
            const result = await check_pending_orders();
            if (result.status === 200 && typeof result.count === 'number') {
                setPendingOrdersCount(result.count);
            }
        } catch (error) {
            if (error.message?.includes('session has expired') || 
                error.message?.includes('Invalid Refresh Token') ||
                error.message?.includes('Already Used') ||
                error.status === 401) {
                // Don't try to refresh, just log and stop trying
                logger.log("Authentication error during pending orders check, stopping intervals");
                // Store a flag to stop future intervals
                window.pendingOrdersAuthError = true;
                // Clear pending orders count on auth error
                setPendingOrdersCount(0);
                return;
            }
            // For other errors, just log but don't stop the interval
            logger.warn("Non-auth error fetching pending orders:", error);
        }
    };

    // Function to fetch unread messages count
    const fetchUnreadMessages = useCallback(async () => {
        if (isCustomer) return;
        try {
            const { status, data } = await getConversations();
            if (status === 200) {
                const convs = data?.data || [];
                const total = convs.reduce((sum, c) => sum + (c.unread_count || 0), 0);
                setUnreadMessagesCount(total);
            }
        } catch (err) {
            logger.warn('Non-auth error fetching unread messages:', err);
        }
    }, [isCustomer]);

    useEffect(() => {
        // Reset auth error flag when component mounts
        window.pendingOrdersAuthError = false;
        
        // Only set up pending orders fetching if user is not a customer
        if (!isCustomer) {
            // Fetch immediately on component mount
            fetchPendingOrders();
            fetchUnreadMessages();
            
            // Set up interval to fetch every 5 minutes with auth error checking
            const interval = setInterval(() => {
                // Check if we've encountered auth errors and should stop
                if (window.pendingOrdersAuthError) {
                    logger.log("Auth error detected, stopping pending orders interval");
                    clearInterval(interval);
                    return;
                }
                fetchPendingOrders();
                fetchUnreadMessages();
            }, 600000); // 10 minutes = 600,000 milliseconds (reduced frequency)

            // Cleanup interval on component unmount
            return () => clearInterval(interval);
        }
    }, [isCustomer, fetchUnreadMessages]);

    // Realtime subscription — update unread badge instantly when any message changes
    const fetchUnreadMessagesRef = useRef(fetchUnreadMessages);
    useEffect(() => { fetchUnreadMessagesRef.current = fetchUnreadMessages; }, [fetchUnreadMessages]);
    useEffect(() => {
        if (!supabase || isCustomer) return;
        const channel = supabase
            .channel('navbar-messages-realtime')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'messages' }, () => {
                fetchUnreadMessagesRef.current();
            })
            .subscribe();
        return () => { supabase.removeChannel(channel); };
    }, [isCustomer, supabase]); // eslint-disable-line react-hooks/exhaustive-deps

    // routes
    const vehiclesRoute = () => {
        if (isAdmin) {
            navigate('/vehicles');
        } else {
            showAlert({
                message: "Admin access only",
                type: 'danger',
                buttons: [
                    { label: 'OK', type: 'danger' },
                ]
            });
        }
    }
    
    const driversRoute = () => {
        if (isAdmin) {
            navigate('/drivers');
        } else {
            showAlert({
                message: "Admin access only",
                type: 'danger',
                buttons: [
                    { label: 'OK', type: 'danger' },
                ]
            });
        }    
    }

    const customersRoute = () => {
        if (isAdmin) {
            navigate('/customers');
        } else {
            showAlert({
                message: "Admin access only",
                type: 'danger',
                buttons: [
                    { label: 'OK', type: 'danger' },
                ]
            });
        }    
    }

    const subcontractorsRoute = () => {
        if (isAdmin) {
            navigate('/subcontractors');
        } else {
            showAlert({
                message: "Admin access only",
                type: 'danger',
                buttons: [
                    { label: 'OK', type: 'danger' },
                ]
            });
        }    
    }

    const warehouseRoute = () => {
        navigate('/warehouse');
    }

    const dashboardRoute = () => {
        navigate('/');
    }

    const showProfile = () => {
        navigate('/profile');
    }

    const openDevTools = () => {
        navigate('/dev');
    }

    const openMessages = () => {
        navigate('/messages');
    }

    const logoutClient = () => {

        // pause before logout to clear local storage
        setTimeout(() => {
            handleLogout();
            navigate('/');
            setIsLoading(false);
        }, 2000);

    }

    const Logout= async ()=>{
        setIsLoading(true);
        // Options to send data to backend
        logger.log("Logging out, sending request to backend");
        const { getOrCreateDeviceId } = await import('./lib/AuthToken');
        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ device_id: getOrCreateDeviceId() })
        };
    
        // Send Request to back-end and log any errors
        fetch('/auth/logout', requestOptions)
        .then(res => {
            logger.log("Response: ", res.status);
    
            // If response 201, show success message
            if (res.status === 201 || res.status === 200) {
                logger.log("Logged out backend successfully");
                logoutClient();
            }
        })
        .catch((err) => {
            // Logout front-end
            logger.log("Backend threw an error: ", err);
            setIsLoading(false);
            showAlert({
                message: "Error logging out, please try again",
                type: 'danger',
                buttons: [
                    { label: 'OK', type: 'danger' },
                ]
            });
        })
    }

    if (isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <ActivityIndicator />
            </div>
        );
    }

    return (
        // <div className="link-container">
        //     <span className='link-button' onClick={!isLoading ? () => driversRoute() : null}>
        //         Drivers
        //     </span>
        //     <span className='link-button' onClick={!isLoading ? () => vehiclesRoute() : null}>
        //         Vehicles
        //     </span>
        //     <span className='link-button' onClick={!isLoading ? () => Logout() : null}>
        //         Logout
        //     </span>
        // </div>

        <div className="link-container">
            <span className="dropdown-button" onClick={toggleDropdown} style={{ position: 'relative' }}>
                <FontAwesomeIcon icon={faList} size={'2x'} style={{ width: 32 }} />
                {(pendingOrdersCount > 0 || unreadMessagesCount > 0) && !showDropdown && (
                    <span 
                        style={{
                            position: 'absolute',
                            top: '-2px',
                            left: '-2px',
                            width: '12px',
                            height: '12px',
                            backgroundColor: 'red',
                            borderRadius: '50%',
                            border: '2px solid red'
                        }}
                    />
                )}
            </span>
    
            <>
            {showDropdown && (
                <div className="modal-overlay" onClick={toggleDropdown}>
                    <div
                        className="dropdown-menu"
                        style={{
                            backgroundColor: theme === 'dark' ? '#191a1a' : '#afafaf',
                            borderColor: theme === 'dark' ? '#fff' : '#191a1a'
                        }}
                    >
                        {!isCustomer && (
                            /* Dashboard — visible to all non-customer users */
                            <span className="dropdown-item" style={{color: theme === 'dark' ? '#fff' : '#191a1a' }} onClick={!isLoading ? () => dashboardRoute() : null}>
                                <FontAwesomeIcon icon={faHouse} style={{ width: 18 }} /> Dashboard
                            </span>
                        )}

                        {!isCustomer && !isService && (
                            <>
                                {/* Drivers */}
                                {(isDev || permissions.perm_users_view) && (
                                    <span className="dropdown-item" style={{color: theme === 'dark' ? '#fff' : '#191a1a' }} onClick={!isLoading ? () => driversRoute() : null}>
                                        <FontAwesomeIcon icon={faDriversLicense} style={{ width: 18 }} /> All Users
                                    </span>
                                )}

                                {/* Vehicles */}
                                {(isDev || permissions.perm_vehicles_view) && (
                                    <span className="dropdown-item" style={{color: theme === 'dark' ? '#fff' : '#191a1a' }} onClick={!isLoading ? () => vehiclesRoute() : null}>
                                        <FontAwesomeIcon icon={faTruck} style={{ width: 18 }} /> Vehicles
                                    </span>
                                )}

                                {/* Customers */}
                                {(features?.routing !== false || features?.warehouse !== false) && (isDev || permissions.perm_customers_view) && (
                                    <span className="dropdown-item" style={{color: theme === 'dark' ? '#fff' : '#191a1a' }} onClick={!isLoading ? () => customersRoute() : null}>
                                        <FontAwesomeIcon icon={faUsersBetweenLines} style={{ width: 18 }} /> Customers
                                    </span>
                                )}

                                {/* Sub-Contractors */}
                                {(isDev || permissions.perm_subcontractors_view) && (
                                    <span className="dropdown-item" style={{color: theme === 'dark' ? '#fff' : '#191a1a' }} onClick={!isLoading ? () => subcontractorsRoute() : null}>
                                        <FontAwesomeIcon icon={faTruckArrowRight} style={{ width: 18 }} /> Sub-Contractors
                                    </span>
                                )}

                                {features?.invoicing !== false && (isDev || isManager || permissions.perm_invoice_view || permissions.perm_invoice_edit) && (
                                    <span className="dropdown-item" style={{color: theme === 'dark' ? '#fff' : '#191a1a' }} onClick={!isLoading ? () => navigate('/invoices') : null}>
                                        <FontAwesomeIcon icon={faFileInvoiceDollar} style={{ width: 18 }} /> Invoicing
                                    </span>
                                )}

                                {/* Messages */}
                                {features?.messaging !== false && (
                                <span className="dropdown-item" style={{color: theme === 'dark' ? '#fff' : '#191a1a', position: 'relative' }} onClick={!isLoading ? () => openMessages() : null}>
                                    <FontAwesomeIcon icon={faComments} style={{ width: 18 }} /> Messages
                                    {unreadMessagesCount > 0 && showDropdown && (
                                        <span
                                            style={{
                                                position: 'absolute',
                                                top: '0',
                                                right: '0',
                                                width: '3px',
                                                height: '100%',
                                                backgroundColor: 'red',
                                                borderRadius: '1px'
                                            }}
                                        />
                                    )}
                                </span>
                                )}
                            </>
                        )}

                        {/* Warehouse */}
                        {!isService && features?.warehouse !== false && (!isCustomer || hasCustomerWarehouseAccess) && (isCustomer || isWarehouse || isDev || permissions.perm_warehouse_view || permissions.perm_warehouse_edit) && (
                        <span className="dropdown-item" style={{color: theme === 'dark' ? '#fff' : '#191a1a', position: 'relative' }} onClick={!isLoading ? () => warehouseRoute() : null}>
                            <FontAwesomeIcon icon={faWarehouse} style={{ width: 18 }} /> Warehouse
                            {pendingOrdersCount > 0 && showDropdown && (
                                <span 
                                    style={{
                                        position: 'absolute',
                                        top: '0',
                                        right: '0',
                                        width: '3px',
                                        height: '100%',
                                        backgroundColor: 'red',
                                        borderRadius: '1px'
                                    }}
                                />
                            )}
                        </span>
                        )}

                        {/* Customer Portal — only for CUSTOMER group */}
                        {isCustomer && (
                        <span className="dropdown-item" style={{color: theme === 'dark' ? '#fff' : '#191a1a' }} onClick={!isLoading ? () => navigate('/customer-portal') : null}>
                            <FontAwesomeIcon icon={faRoute} style={{ width: 18 }} /> My Deliveries
                        </span>
                        )}

                        {/* Profile */}
                        <span className="dropdown-item" style={{color: theme === 'dark' ? '#fff' : '#191a1a' }} onClick={!isLoading ? () => showProfile() : null}>
                            <FontAwesomeIcon icon={faUserCircle} style={{ width: 18 }} /> Profile
                        </span>

                        {/* My Training — for admin users with computer-only access */}
                        {features?.training !== false && isAdmin && (
                        <span className="dropdown-item" style={{color: theme === 'dark' ? '#fff' : '#191a1a' }} onClick={!isLoading ? () => navigate('/my-training') : null}>
                            <FontAwesomeIcon icon={faGraduationCap} style={{ width: 18 }} /> My Training
                        </span>
                        )}

                        {/* Theme */}
                        <span className="dropdown-item" style={{color: theme === 'dark' ? '#fff' : '#191a1a' }} onClick={!isLoading ? () => toggleTheme() : null}>
                            {theme === 'light' ? <FontAwesomeIcon icon={faSun} style={{ width: 18 }} /> :<FontAwesomeIcon icon={faMoon} style={{ width: 18 }} />} Theme
                        </span>

                        {!isService && (isDev || isManager || permissions.perm_invoice_edit) && (
                        <span className="dropdown-item" style={{color: theme === 'dark' ? '#fff' : '#191a1a' }} onClick={!isLoading ? () => openDevTools() : null}>
                            <FontAwesomeIcon icon={faCode} style={{ width: 18 }} /> Control Panel
                        </span>
                        )}

                        {/* Remote Assistance */}
                        <RemoteAssistanceItem theme={theme} />

                        {/* Logout */}
                        <span className="dropdown-item" style={{color: theme === 'dark' ? '#fff' : '#191a1a' }} onClick={!isLoading ? () => Logout() : null}>
                            <FontAwesomeIcon icon={faSignOutAlt} style={{ width: 18 }} /> Logout
                        </span>
                    </div>
                </div>
            )}
            </>
        </div>
    )
}

const LoggedOutLinks = () => {
    const sendEmail = () => {
        window.location.href = "mailto:holliescoding@gmail.com?subject=Inquiry";
    };

    return (
        <div className="link-container">
            <span className="link-button" onClick={sendEmail}>
                Contact
            </span>
        </div>
    );
};



const NavBar = () => {

    // Login Authorization
    const { loggedIn, companyLogo } = useAuthProvider();
    const { showAlert } = useAlert();
    
    const { theme } = useTheme();
    const logoSrc = companyLogo || (theme === 'dark' ? LogoDark : Logo);

    // Theme
    const navigate = useNavigate();

    // Easter Egg State
    const [showTetris, setShowTetris] = useState(false);

    const homeRoute = () => navigate('/');
    
    // Easter Egg Handlers
    const handleEasterEgg = () => {
        if (loggedIn) {
            // Show congratulations alert
            showAlert({
                message: '🎉 Congratulations! You found the secret Easter egg! 🎉',
                type: 'success',
                buttons: [{ label: 'OK', type: 'primary' }]
            });
            
            // Brief delay then show rules and start game
            setTimeout(() => {
                showAlert({
                    message: '🎮 TETRIS RULES: Use ← → arrow keys to move pieces, ↓ arrow key to drop faster, ↑ arrow key to rotate pieces. Clear lines to score points and beat the global high score!',
                    type: 'success',
                    buttons: [{ label: 'Start Game', type: 'primary' }]
                });
            }, 1000);
            
            // Start the game
            setTimeout(() => {
                setShowTetris(true);
            }, 3000);
        }
    };

    const handleGameOver = (finalScore) => {
        showAlert({
            message: `Game Over! Your final score: ${finalScore}`,
            type: 'warning',
            buttons: [{ label: 'OK', type: 'primary' }]
        });
    };

    const closeTetris = () => {
        setShowTetris(false);
    };

    return (

        <Navbar id={theme} className="navContainer">

            {/* Nav Title */}
            <Nav className="link-buttons">
                <span className="nav-logo" onClick={homeRoute}>
                    <img 
                        src={loggedIn ? logoSrc : (theme === 'dark' ? LogoDark : Logo)} 
                        alt="Logo" 
                        style={{ borderRadius: "10px" }} 
                        width={260} 
                        height={60} 
                    />
                </span>
                <span className="day-container">
                    <Time onEasterEgg={handleEasterEgg} />
                </span>
                {/* Change nav bar depending on login status */}
                {loggedIn ? <LoggedInLinks /> : <LoggedOutLinks />}
            </Nav>

            {/* Easter Egg Tetris Game */}
            {showTetris && (
                <TetrisGame 
                    theme={theme}
                    onClose={closeTetris}
                    onGameOver={handleGameOver}
                />
            )}

        </Navbar>
    );
}

export default NavBar;